import { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { ArrowLeft, Play, Pause, Download, Home, User, ExternalLink } from "lucide-react";

const ContentView = () => {
  const { id } = useParams();
  const [content, setContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);
  const { token, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/contents/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setContent(response.data);
      } catch (error) {
        console.error("Error fetching content:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchContent();
  }, [id, token]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    const time = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F9F7F2] px-6 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="skeleton h-8 w-32 mb-8 rounded-full" />
          <div className="skeleton h-64 mb-6 rounded-2xl" />
          <div className="skeleton h-8 w-48 mb-4 rounded-lg" />
          <div className="skeleton h-24 rounded-lg" />
        </div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="min-h-screen bg-[#F9F7F2] flex items-center justify-center px-6">
        <div className="text-center">
          <h2 className="font-serif text-2xl text-[#3F2A24] mb-4">Contenu non trouvé</h2>
          <Link to="/sanctuary" className="btn-sacred">
            Retour au sanctuaire
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F7F2] pb-24">
      {/* Header */}
      <header className="px-6 pt-8 pb-4">
        <div className="max-w-2xl mx-auto">
          <button 
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-[#4B3329] hover:text-[#C5A059] transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            <span className="text-sm tracking-wide">Retour</span>
          </button>
        </div>
      </header>

      {/* Content */}
      <section className="px-6 py-4">
        <div className="max-w-2xl mx-auto animate-fade-in-up">
          {/* Title */}
          <h1 className="font-serif text-2xl md:text-3xl text-[#3F2A24] mb-2">
            {content.title}
          </h1>
          {content.description && (
            <p className="text-[#4B3329] mb-6">
              {content.description}
            </p>
          )}
          <div className="divider-gold mb-8" />

          {/* Audio Player */}
          {content.type === 'audio' && content.media_url && (
            <div className="card-sacred p-6 mb-8">
              <audio
                ref={audioRef}
                src={content.media_url}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onEnded={() => setIsPlaying(false)}
              />
              
              {/* Custom Player UI */}
              <div className="flex items-center gap-4">
                <button
                  onClick={togglePlay}
                  className="w-14 h-14 rounded-full bg-[#C5A059] flex items-center justify-center text-[#F9F7F2] hover:bg-[#D6B16A] transition-colors flex-shrink-0"
                  data-testid="play-pause-btn"
                >
                  {isPlaying ? (
                    <Pause className="w-6 h-6" fill="#F9F7F2" />
                  ) : (
                    <Play className="w-6 h-6 ml-1" fill="#F9F7F2" />
                  )}
                </button>
                
                <div className="flex-1">
                  <input
                    type="range"
                    min={0}
                    max={duration || 100}
                    value={currentTime}
                    onChange={handleSeek}
                    className="audio-progress w-full"
                    data-testid="audio-progress"
                  />
                  <div className="flex justify-between mt-2 text-xs text-[#8C6A4A]">
                    <span>{formatTime(currentTime)}</span>
                    <span>{formatTime(duration)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Video Embed */}
          {content.type === 'video' && content.media_url && (
            <div className="rounded-2xl overflow-hidden mb-8 aspect-video bg-[#3F2A24]">
              {content.media_url.includes('youtube') || content.media_url.includes('youtu.be') ? (
                <iframe
                  src={content.media_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                  title={content.title}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : content.media_url.includes('vimeo') ? (
                <iframe
                  src={content.media_url.replace('vimeo.com/', 'player.vimeo.com/video/')}
                  title={content.title}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <video
                  src={content.media_url}
                  controls
                  className="w-full h-full"
                  data-testid="video-player"
                />
              )}
            </div>
          )}

          {/* Photo */}
          {content.type === 'photo' && content.media_url && (
            <div className="rounded-2xl overflow-hidden mb-8">
              <img
                src={content.media_url}
                alt={content.title}
                className="w-full h-auto"
                data-testid="photo-image"
              />
            </div>
          )}

          {/* Document */}
          {content.type === 'document' && content.media_url && (
            <a
              href={content.media_url}
              target="_blank"
              rel="noopener noreferrer"
              className="card-sacred p-6 mb-8 flex items-center gap-4 hover:border-[#C5A059]/30"
              data-testid="document-download"
            >
              <div className="w-14 h-14 rounded-full bg-[#C5A059]/10 flex items-center justify-center">
                <Download className="w-6 h-6 text-[#C5A059]" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <p className="font-medium text-[#3F2A24]">Télécharger le document</p>
                <p className="text-sm text-[#8C6A4A]">Clique pour ouvrir</p>
              </div>
              <ExternalLink className="w-5 h-5 text-[#8C6A4A]" strokeWidth={1.5} />
            </a>
          )}

          {/* Long Text Content */}
          {content.type === 'text' && content.content_text && (
            <div className="prose prose-lg max-w-none">
              <div 
                className="text-[#3F2A24] leading-relaxed whitespace-pre-wrap font-light"
                style={{ fontFamily: "'Manrope', sans-serif" }}
                data-testid="text-content"
              >
                {content.content_text}
              </div>
            </div>
          )}

          {/* Also show text content for any type that has it */}
          {content.type !== 'text' && content.content_text && (
            <div className="mt-8 pt-8 border-t border-[#C5A059]/10">
              <div 
                className="text-[#3F2A24] leading-relaxed whitespace-pre-wrap"
                data-testid="additional-text"
              >
                {content.content_text}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Bottom Navigation */}
      <nav className="nav-sacred" data-testid="bottom-nav">
        <Link to="/sanctuary" className="nav-item">
          <Home className="w-5 h-5" strokeWidth={1.5} />
          <span>Accueil</span>
        </Link>
        <button onClick={handleLogout} className="nav-item">
          <User className="w-5 h-5" strokeWidth={1.5} />
          <span>Sortir</span>
        </button>
      </nav>
    </div>
  );
};

export default ContentView;
